import { LoginDescriptor } from '@/features/logins/loginsSlice';
import { HotelExecutives, HotelInfo } from '@/features/projects/projectsSlice';

// ---------------------------------------------------------------------------
// MW light — the executives list and the light project rows (ticket #91).
//
// Everything here is pure: the server is the authority on who may be an MW
// light user (§3.3 lists its refusals verbatim), and this module only mirrors
// those rules so the dropdown does not offer a name the save would bounce.
// When the two ever disagree the server wins and its sentence is shown as is.
// ---------------------------------------------------------------------------

// §3.1 — a light row is the minimal project shape the unfiltered list carries
// for a project the caller is MW light on: no stats, no urls, no options.
export function isLightRow(proj?: HotelInfo | null): boolean {
  return !!(proj && proj.mw_light === true);
}

export function findLightRow(id: string, projects: HotelInfo[]): HotelInfo | null {
  for (const proj of projects || []) {
    if (	proj.id === id &&
			isLightRow(proj)
		 )
      return proj;
  }

  return null;
}

// §2.1 — roles the server refuses outright. Every other role, including
// `user` and the empty string, is fine.
const blockedRoles: { [role: string]: string } = {
  admin:		"has the admin role",
  api:			"has the api role",
  reseller:		"has the reseller role",
};

// §2.1 / §3.3 — privileges that reach every project. `SetGlobalUsers` is the
// legacy name of `ChangeGlobalUsers` and counts as it.
const blockedPrivileges: { [priv: string]: string } = {
  SeeAllProjects:		"can see all projects",
  ViewLogs:				"can read the audit log of every project",
  DeleteProjects:		"can delete any project",
  ChangeGlobalUsers:	"can change every user",
  SetGlobalUsers:		"can change every user",
};

export const mwLightMaxUsers = 100;

// An SSO name carries the realm separator. Such users never appear in
// /logins.json, but a stored list from older data can still name one.
export function isSsoName(username?: string): boolean {
  return !!username && username.indexOf("%") > -1;
}

// The reason the server would refuse this descriptor, in the wording of §3.3,
// or "" when it would accept them. Only the user's OWN properties are judged
// here — project membership is a separate question (see mwLightListConflict).
export function mwLightIneligibleReason(desc?: LoginDescriptor | null): string {
  if (!desc)
    return "does not exist";

  if (isSsoName(desc.username))
    return "signs in through single sign-on";

  if (	desc.local_project_id &&
		desc.local_project_id !== ""
	 )
    return "is a user of a single project";

  const role = (desc.role || "").toLowerCase();
  if (blockedRoles[role])
    return blockedRoles[role];

  for (const priv of desc.privileges || []) {
    if (blockedPrivileges[priv])
      return blockedPrivileges[priv];
  }

  return "";
}

export function isEligibleMwLightUser(desc?: LoginDescriptor | null): boolean {
  return mwLightIneligibleReason(desc) === "";
}

// The three lists a person may not be in at the same time as MW light. The
// names here are the ones the server's own sentences use.
export const mwLightRivalLists: { key: keyof HotelExecutives; label: string }[] = [
  { key: "partner",		label: "Partner" },
  { key: "chain_manager",	label: "Chain manager" },
  { key: "manager",		label: "Manager" },
];

// "Manager", "Partner", … when this name is already an executive of THIS
// project, or "" when it is not. Checked against the list as it stands in the
// editor, so the warning appears before the save round trip.
export function mwLightListConflict(username: string, execs?: HotelExecutives | null): string {
  if (	!username ||
		!execs
	 )
    return "";

  for (const list of mwLightRivalLists) {
    const names = execs[list.key];

    if (names && names.indexOf(username) > -1)
      return list.label;
  }

  return "";
}

export function getMwLight(execs?: HotelExecutives | null): string[] {
  if (	!execs ||
		!Array.isArray(execs.mw_light)
	 )
    return [];

  return execs.mw_light;
}

// §2.1 — names in MW light AND in one of the other three lists. Only possible
// in data written before the server enforced it; an admin save stays refused
// until one side is removed, so the page blocks it too.
export interface MwLightOverlap {
  username:				string;
  list:					string;		// "Partner" | "Chain manager" | "Manager"
}

export function mwLightOverlaps(execs?: HotelExecutives | null): MwLightOverlap[] {
  const out: MwLightOverlap[] = [];

  for (const username of getMwLight(execs)) {
    const list = mwLightListConflict(username, execs);

    if (list)
      out.push({ username: username, list: list });
  }

  return out;
}

export function findLoginDescriptor(username: string, descriptors?: LoginDescriptor[] | null): LoginDescriptor | null {
  for (const desc of descriptors || []) {
    if (desc.username === username)
      return desc;
  }

  return null;
}

// §2.1 "Show, don't hide". A listed name is never silently dropped: it is
// shown with why it cannot stay, and it can be removed.
export type MwLightEntryState = "ok" | "deleted" | "not_allowed";

export function mwLightEntryState(username: string, descriptors?: LoginDescriptor[] | null): MwLightEntryState {
  const desc = findLoginDescriptor(username, descriptors);

  // A descriptor list that has not loaded yet is not evidence of deletion —
  // treat every name as fine until /logins.json answers.
  if (	!descriptors ||
		descriptors.length === 0
	 )
    return "ok";

  if (!desc)
    return "deleted";

  return isEligibleMwLightUser(desc) ? "ok" : "not_allowed";
}

// "anna", "anna (deleted)", "anna (not allowed)".
//
// The username exactly as /logins.json carries it, and nothing else. The other
// three executive lists append the role ("anna (user)"), but this one does not:
// the only suffixes here are the two §2.1 prescribes for a name that cannot
// stay, so a bracket in this box always means a problem rather than decoration.
export function mwLightEntryLabel(username: string, descriptors?: LoginDescriptor[] | null): string {
  switch (mwLightEntryState(username, descriptors)) {
  case "deleted":
    return username + " (deleted)";
  case "not_allowed":
    return username + " (not allowed)";
  default:
    return username;
  }
}

// §2.1 last bullet / §2.4 — the warning an admin sees when the list cannot do
// its job: Netflix survives the automatic clear and nobody can press the
// button. An EMPTY list is not this case: a hotel with no MW light users is
// not an MW light hotel at all, so the automatic clear still takes Netflix.
export function hasActiveMwLightUser(execs?: HotelExecutives | null, descriptors?: LoginDescriptor[] | null): boolean {
  for (const username of getMwLight(execs)) {
    if (mwLightEntryState(username, descriptors) === "ok")
      return true;
  }

  return false;
}

export const noActiveMwLightUserWarning =
  "Netflix is kept on automatic checkout, but no active MW light user can clear it.";

export function needsNoActiveMwLightUserWarning(execs?: HotelExecutives | null, descriptors?: LoginDescriptor[] | null): boolean {
  return	getMwLight(execs).length > 0 &&
			!hasActiveMwLightUser(execs, descriptors);
}

// §2.1 — the dropdown source. Every name /logins.json offers, minus the ones
// the server would refuse for who they are, minus this project's own Partner /
// Chain manager / Manager, minus the owner and (when the caller has it to
// hand) the chain managers of the project's group.
//
// A name ALREADY in the stored list is kept regardless: HeroUI's Select drops
// a selected key that is not among its items, and silently dropping it would
// turn an explicit list into a shorter one on the next save.
export interface MwLightOptionSource {
  descriptors:			LoginDescriptor[];
  executives?:			HotelExecutives | null;
  owner?:				string;
  groupChainManagers?:	string[];
  selected?:			string[];
}

export function mwLightCandidates(src: MwLightOptionSource): string[] {
  const selected = src.selected || [];
  const out: string[] = [];
  const seen: { [name: string]: boolean } = {};

  for (const desc of src.descriptors || []) {
    const username = desc.username;

    if (	!username ||
			seen[username]
		 )
      continue;

    seen[username] = true;

    if (selected.indexOf(username) > -1) {
      out.push(username);
      continue;
    }

    if (!isEligibleMwLightUser(desc))
      continue;

    if (mwLightListConflict(username, src.executives))
      continue;

    if (	src.owner &&
			src.owner === username
		 )
      continue;

    if ((src.groupChainManagers || []).indexOf(username) > -1)
      continue;

    out.push(username);
  }

  // A stored name whose descriptor is gone still has to be offerable, or it
  // could never be removed (§2.1 "Show, don't hide").
  for (const username of selected) {
    if (	username &&
			!seen[username]
		 ) {
      seen[username] = true;
      out.push(username);
    }
  }

  return out;
}

export const mwLightRivalTooltip = "MW light user on this project";
